import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { User } from '@supabase/supabase-js';
import { Settings as SettingsIcon, Loader2, UserPlus, Sparkles, Check, Clock } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAchievements } from '../hooks/useAchievements';
import { useFriends } from '../hooks/useFriends';
import { useStreak } from '../hooks/useStreak';
import { useFriendRequests, describeSendRequestError } from '../hooks/useFriendRequests';
import { useMyProfile } from '../hooks/useProfile';
import { Avatar } from '../components/ui/Avatar';
import { MedalIcon } from '../components/ui/MedalIcon';
import { Eyebrow } from '../components/ui/Eyebrow';
import { Toast } from '../components/ui/Toast';
import { MedalDetailModal } from '../components/MedalDetailModal';
import { TOTAL_STICKERS } from '../data/stickers';
import type { Achievement, AchievementCategory, UserAchievement } from '../types/social';
import type { TabKey } from '../components/layout/Tabs';

interface Props {
  user: User;
  embedded?: boolean;
  onChangeTab?: (tab: TabKey) => void;
}

const CATEGORY_FILTERS: Array<{ key: 'all' | AchievementCategory; label: string }> = [
  { key: 'all', label: 'Todas' },
  { key: 'collection', label: 'Coleção' },
  { key: 'confed', label: 'Confederações' },
  { key: 'social', label: 'Social' },
  { key: 'time', label: 'Tempo' },
  { key: 'event', label: 'Eventos' },
  { key: 'special', label: 'Especial' },
];

interface FofSuggestion {
  user_id: string;
  display_name: string;
  username: string;
  mutual_friends_count: number;
}

export default function PerfilPage({ user, embedded }: Props) {
  const userName = (user.user_metadata?.full_name as string) || user.email || 'Você';
  const { profile } = useMyProfile(user.id);
  const { friends } = useFriends(user.id, user.email ?? '', userName);
  const { catalog, earned, loading: achLoading } = useAchievements(user.id);
  const { streak } = useStreak(user.id);
  const friendIds = useMemo(() => friends.map((f) => f.id), [friends]);
  const {
    incoming,
    outgoing,
    sendRequest,
  } = useFriendRequests(user.id);

  const [stickers, setStickers] = useState<{ owned: number; trades: number }>({
    owned: 0,
    trades: 0,
  });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [{ data: stk }, { count: tradesCount }] = await Promise.all([
        supabase
          .from('stickers')
          .select('sticker_code, count')
          .eq('user_id', user.id),
        supabase
          .from('trades')
          .select('id', { count: 'exact', head: true })
          .or(`user_a.eq.${user.id},user_b.eq.${user.id}`)
          .eq('status', 'confirmed'),
      ]);
      if (cancelled) return;
      const ownedCount = (stk ?? []).filter(
        (r: { count: number }) => r.count >= 1
      ).length;
      setStickers({ owned: ownedCount, trades: tradesCount ?? 0 });
    })();
    return () => {
      cancelled = true;
    };
  }, [user.id]);

  const [filter, setFilter] = useState<'all' | AchievementCategory>('all');
  const [selectedMedal, setSelectedMedal] = useState<Achievement | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const earnedById = useMemo(() => {
    const m = new Map<string, UserAchievement>();
    for (const e of earned) m.set(e.achievement_id, e);
    return m;
  }, [earned]);

  const filteredCatalog = useMemo(() => {
    if (filter === 'all') return catalog;
    return catalog.filter((a) => a.category === filter);
  }, [catalog, filter]);

  // FOF: lazy load só quando esta tela for aberta (já é o caso por estar em mount aqui).
  const [suggestions, setSuggestions] = useState<FofSuggestion[]>([]);
  const [loadingSugg, setLoadingSugg] = useState(true);
  const [optimisticSent, setOptimisticSent] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoadingSugg(true);
      const { data, error } = await supabase.rpc('get_fof_suggestions', {
        p_user_id: user.id,
      });
      if (cancelled) return;
      if (error || !data) {
        setSuggestions([]);
      } else {
        setSuggestions(data as FofSuggestion[]);
      }
      setLoadingSugg(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [user.id]);

  const outgoingTargetIds = useMemo(() => outgoing.map((r) => r.to_user_id), [outgoing]);
  const incomingFromIds = useMemo(() => incoming.map((r) => r.from_user_id), [incoming]);

  const handleAddSuggestion = async (s: FofSuggestion) => {
    const res = await sendRequest(s.user_id);
    if (res.ok) {
      setOptimisticSent((prev) => [...prev, s.user_id]);
      setToast(`Pedido enviado para ${s.display_name}.`);
    } else {
      setToast(describeSendRequestError(res.reason));
    }
  };

  const pct = TOTAL_STICKERS > 0 ? Math.round((stickers.owned / TOTAL_STICKERS) * 100) : 0;
  const earnedCount = earned.length;

  const username = profile?.username ?? '';

  const Body = (
    <>
      {toast && <Toast message={toast} onClose={() => setToast(null)} />}

      <section className="bg-green-700 text-white rounded-2xl px-4 py-5 mb-5 flex items-center gap-4">
        <Avatar name={userName} size="xl" className="ring-2 ring-white/20" />
        <div className="flex-1 min-w-0">
          <p className="font-display font-bold text-[20px] leading-tight truncate">{userName}</p>
          {username && (
            <p className="text-[14px] text-white/75 truncate mt-0.5">@{username}</p>
          )}
        </div>
        <Link
          to="/configuracoes"
          aria-label="Configurações"
          className="inline-flex items-center justify-center w-9 h-9 rounded-md border-[1.5px] border-white/40 hover:bg-white/10 btn-focus"
        >
          <SettingsIcon size={15} strokeWidth={2.25} />
        </Link>
      </section>

      <section className="grid grid-cols-4 gap-2 mb-6">
        <StatCell label="Figurinhas" value={`${stickers.owned}`} sub={`/${TOTAL_STICKERS}`} />
        <StatCell label="Amigos" value={`${friends.length}`} />
        <StatCell label="Trocas" value={`${stickers.trades}`} />
        <StatCell label="Streak" value={`${streak}`} sub="dias" />
      </section>

      <section className="mb-7">
        <div className="flex items-baseline justify-between mb-2">
          <Eyebrow>Álbum</Eyebrow>
          <span className="font-display font-bold text-h3 text-green-700">{pct}%</span>
        </div>
        <div className="w-full h-2 bg-ink-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-green-700 transition-[width] duration-progress ease-out"
            style={{ width: `${Math.min(100, pct)}%` }}
          />
        </div>
      </section>

      <section className="mb-7">
        <div className="flex items-baseline justify-between mb-3">
          <Eyebrow>Conquistas</Eyebrow>
          <span className="text-caption text-ink-500 font-semibold">
            {earnedCount}/{catalog.length}
          </span>
        </div>

        <div
          className="flex gap-1.5 overflow-x-auto -mx-4 px-4 pb-2 mb-4"
          style={{ scrollbarWidth: 'none' }}
        >
          {CATEGORY_FILTERS.map((c) => {
            const active = filter === c.key;
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => setFilter(c.key)}
                className={[
                  'shrink-0 rounded-full px-3 py-1.5 text-caption font-semibold border transition-colors',
                  active
                    ? 'bg-green-700 text-white border-green-700'
                    : 'bg-white text-ink-700 border-ink-300 hover:border-green-700',
                ].join(' ')}
              >
                {c.label}
              </button>
            );
          })}
        </div>

        {achLoading ? (
          <div className="grid grid-cols-4 gap-3">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="aspect-square bg-ink-100 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : filteredCatalog.length === 0 ? (
          <p className="text-small text-ink-500 italic text-center py-6">
            Sem medalhas nessa categoria.
          </p>
        ) : (
          <div className="grid grid-cols-4 gap-3">
            {filteredCatalog.map((m) => {
              const locked = !earnedById.has(m.id);
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setSelectedMedal(m)}
                  aria-label={m.name}
                  className="flex flex-col items-center gap-1.5 p-2 rounded-xl bg-white border border-ink-300 hover:border-green-700 transition-colors btn-focus"
                >
                  <MedalIcon medal={m} size={56} locked={locked} />
                  <span
                    className={[
                      'font-display font-semibold text-[11px] leading-tight text-center line-clamp-2',
                      locked ? 'text-ink-500' : 'text-ink-900',
                    ].join(' ')}
                  >
                    {m.name}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </section>

      <section className="mb-7">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles size={16} strokeWidth={2.25} className="text-green-700" />
          <h2 className="font-display font-bold text-h3 text-ink-900">Conheça pessoas</h2>
        </div>
        <p className="text-small text-ink-500 mb-3 leading-snug">
          Pessoas que têm amigos em comum com você.
        </p>

        {loadingSugg ? (
          <div className="bg-white border border-ink-300 rounded-xl p-3 space-y-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-12 bg-ink-100 rounded animate-pulse" />
            ))}
          </div>
        ) : suggestions.length === 0 ? (
          <div className="bg-white border border-dashed border-ink-300 rounded-xl py-6 px-4 text-center">
            <p className="text-small text-ink-500 leading-snug">
              Convide mais amigos para ver sugestões aqui.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {suggestions.map((s) => {
              const isFriend = friendIds.includes(s.user_id);
              const sentNow = optimisticSent.includes(s.user_id);
              const alreadyOutgoing = outgoingTargetIds.includes(s.user_id);
              const alreadyIncoming = incomingFromIds.includes(s.user_id);
              return (
                <article
                  key={s.user_id}
                  className="bg-white border border-ink-300 rounded-xl p-3 flex items-center gap-3"
                >
                  <Avatar name={s.display_name} size="md" />
                  <div className="flex-1 min-w-0">
                    <p className="font-display font-bold text-[14px] text-ink-900 truncate">
                      {s.display_name}
                    </p>
                    {s.username && (
                      <p className="text-caption text-ink-500 truncate">@{s.username}</p>
                    )}
                    <p className="text-caption font-display font-semibold text-green-700 mt-0.5">
                      {s.mutual_friends_count}{' '}
                      {s.mutual_friends_count === 1 ? 'amigo' : 'amigos'} em comum
                    </p>
                  </div>
                  {isFriend ? (
                    <button
                      type="button"
                      disabled
                      className="px-3 h-9 rounded-md border-[1.5px] border-green-700 text-green-700 font-display font-bold text-[12px] inline-flex items-center gap-1.5 cursor-not-allowed"
                    >
                      <Check size={14} strokeWidth={2.5} /> Amigos
                    </button>
                  ) : sentNow || alreadyOutgoing ? (
                    <button
                      type="button"
                      disabled
                      className="px-3 h-9 rounded-md bg-ink-100 text-ink-500 font-display font-bold text-[12px] inline-flex items-center gap-1.5 cursor-not-allowed"
                    >
                      <Clock size={14} strokeWidth={2.25} /> Enviado
                    </button>
                  ) : alreadyIncoming ? (
                    <span className="px-3 h-9 inline-flex items-center rounded-md bg-gold-100 text-ink-900 font-display font-bold text-[12px]">
                      Pediu você
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleAddSuggestion(s)}
                      className="px-3 h-9 rounded-md bg-green-700 text-white font-display font-bold text-[12px] inline-flex items-center gap-1.5 hover:bg-green-900 btn-focus"
                    >
                      <UserPlus size={14} strokeWidth={2.25} /> Adicionar
                    </button>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </section>

      <Link
        to="/configuracoes"
        className="block w-full bg-white border border-ink-300 rounded-xl py-3 px-4 text-center font-display font-bold text-[13px] text-ink-900 hover:bg-ink-100/40 btn-focus inline-flex items-center justify-center gap-2"
      >
        <SettingsIcon size={14} strokeWidth={2.25} />
        Configurações
      </Link>

      <MedalDetailModal
        medal={selectedMedal}
        earnedAt={selectedMedal ? earnedById.get(selectedMedal.id)?.earned_at ?? null : null}
        locked={selectedMedal ? !earnedById.has(selectedMedal.id) : false}
        onClose={() => setSelectedMedal(null)}
      />
    </>
  );

  if (embedded) {
    return <div className="pb-4">{Body}</div>;
  }

  return (
    <div className="min-h-screen bg-paper">
      <main className="max-w-app mx-auto px-4 pt-6 pb-32">
        {achLoading && earnedCount === 0 ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={28} className="animate-spin text-green-700" />
          </div>
        ) : (
          Body
        )}
      </main>
    </div>
  );
}

interface StatCellProps {
  label: string;
  value: string;
  sub?: string;
}

function StatCell({ label, value, sub }: StatCellProps) {
  return (
    <div className="bg-white border border-ink-300 rounded-xl px-2 py-3 text-center shadow-xs">
      <div className="font-display font-bold text-[18px] text-ink-900 leading-tight">
        {value}
        {sub && <span className="text-caption text-ink-500 font-semibold ml-0.5">{sub}</span>}
      </div>
      <div className="text-[10px] uppercase tracking-wider text-ink-500 font-bold mt-1">
        {label}
      </div>
    </div>
  );
}
